
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ViewContractDialog } from '@/components/sales/ViewContractDialog';
import {
  Search,
  FileText,
  MoreVertical,
  Plus,
  DollarSign,
  Clock,
  AlertCircle,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { contractService, Contract, ContractFormData, ContractStats } from '@/services/contract.service';
import { clientService, Client } from '@/services/client.service';
import { useAuthStore } from '@/stores/authStore';


const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Expired':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function Contracts() {
  const { hasSubModulePermission } = useAuthStore();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [stats, setStats] = useState<ContractStats>({
    activeContracts: 0,
    totalValue: 0,
    expiringSoon: 0,
    pendingRenewals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContracts, setTotalContracts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState<Contract | null>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [newContractClientId, setNewContractClientId] = useState<string>('');
  const [editContractClientId, setEditContractClientId] = useState<string>('');

  const [newContract, setNewContract] = useState<ContractFormData>({
    title: '',
    client: '',
    value: 0,
    status: 'Active',
    startDate: '',
    endDate: '',
    type: 'Fixed Price',
    renewalDate: '',
  });

  const [editContract, setEditContract] = useState<ContractFormData>({
    title: '',
    client: '',
    value: 0,
    status: 'Active',
    startDate: '',
    endDate: '',
    type: 'Fixed Price',
    renewalDate: '',
  });

  const [newContractErrors, setNewContractErrors] = useState({
    title: '',
    client: '',
    startDate: '',
    endDate: '',
  });
  const [editContractErrors, setEditContractErrors] = useState({
    title: '',
    client: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadContracts();
    loadStats();
  }, [currentPage, pageSize, searchQuery]);

  useEffect(() => {
    if (showAdd || showEdit) {
      loadClients();
    }
  }, [showAdd, showEdit]);

  useEffect(() => {
    if (showEdit && selected && clients.length > 0) {
      const foundClient = clients.find(c => c.name === selected.client);
      if (foundClient) {
        setEditContractClientId(foundClient.id || foundClient._id || '');
      }
    }
  }, [showEdit, selected, clients]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const response = await contractService.getContracts({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
      });

      setContracts(response.data.map(contract => ({
        ...contract,
        id: contract._id || contract.id,
      })));
      setTotalPages(Math.ceil(response.total / pageSize));
      setTotalContracts(response.total);
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Load all contracts to calculate accurate stats
      const allContractsResponse = await contractService.getContracts({
        limit: 1000,
      });
      const allContracts = allContractsResponse.data || [];

      // Calculate stats from contracts data
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      thirtyDaysFromNow.setHours(23, 59, 59, 999);

      let activeContracts = 0;
      let totalValue = 0;
      let expiringSoon = 0;
      let pendingRenewals = 0;

      allContracts.forEach((contract) => {
        // Count active contracts
        if (contract.status === 'Active') {
          activeContracts++;
          totalValue += contract.value || 0;
        }

        // Check if contract is expiring soon (endDate within next 30 days)
        if (contract.endDate) {
          const endDate = new Date(contract.endDate);
          endDate.setHours(0, 0, 0, 0);

          if (endDate >= today && endDate <= thirtyDaysFromNow && contract.status === 'Active') {
            expiringSoon++;
          }
        }

        // Check for pending renewals
        // Contracts that are expiring soon or have renewal date in the past/upcoming
        if (contract.renewalDate) {
          const renewalDate = new Date(contract.renewalDate);
          renewalDate.setHours(0, 0, 0, 0);

          // If renewal date is in the past or within next 30 days, and contract is active
          if (renewalDate <= thirtyDaysFromNow && contract.status === 'Active') {
            pendingRenewals++;
          }
        } else if (contract.endDate) {
          // If no renewal date but contract is expiring soon, it needs renewal attention
          const endDate = new Date(contract.endDate);
          endDate.setHours(0, 0, 0, 0);

          if (endDate >= today && endDate <= thirtyDaysFromNow && contract.status === 'Active') {
            pendingRenewals++;
          }
        }
      });

      setStats({
        activeContracts,
        totalValue,
        expiringSoon,
        pendingRenewals,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      // Fallback to backend stats if available
      try {
        const statsData = await contractService.getContractStats();
        setStats(statsData);
      } catch (fallbackError) {
        console.error('Error loading fallback stats:', fallbackError);
      }
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const clientsResponse = await clientService.getClients({ limit: 1000 });
      const clientsData = clientsResponse.data || clientsResponse || [];

      if (Array.isArray(clientsData) && clientsData.length > 0) {
        setClients(clientsData);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const startIndex = contracts.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex = contracts.length === 0 ? 0 : Math.min(startIndex + pageSize, totalContracts || contracts.length);

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchQuery("");
    await loadContracts();
    await loadStats();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const debouncedSearch = (() => {
    let timeoutId: NodeJS.Timeout;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(query);
        setCurrentPage(1);
      }, 500);
    };
  })();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const openView = (c: Contract) => { setSelected(c); setShowView(true); };
  const openEdit = (c: Contract) => {
    setSelected(c);
    setEditContract({
      title: c.title,
      client: c.client,
      value: c.value,
      status: c.status,
      startDate: c.startDate,
      endDate: c.endDate,
      type: c.type,
      renewalDate: c.renewalDate,
    });
    setEditContractClientId('');
    setShowEdit(true);
  };
  const openDelete = (c: Contract) => { setSelected(c); setShowDelete(true); };

  const handleCreate = async () => {
    const selectedClient = clients.find(c => (c.id === newContractClientId) || (c._id === newContractClientId));
    const clientName = selectedClient ? selectedClient.name : '';

    const errors = {
      title: !newContract.title ? 'Title is required' : '',
      client: !newContractClientId ? 'Client is required' : '',
      startDate: !newContract.startDate ? 'Start Date is required' : '',
      endDate: !newContract.endDate ? 'End Date is required' : '',
    };
    setNewContractErrors(errors);

    if (!newContract.title || !newContractClientId || !newContract.startDate || !newContract.endDate) {
      return;
    }

    try {
      setSubmitting(true);
      const contractData = {
        ...newContract,
        client: clientName,
      };
      await contractService.createContract(contractData);

      setNewContract({
        title: '',
        client: '',
        value: 0,
        status: 'Active',
        startDate: '',
        endDate: '',
        type: 'Fixed Price',
        renewalDate: '',
      });
      setNewContractClientId('');
      setNewContractErrors({ title: '', client: '', startDate: '', endDate: '' });
      setShowAdd(false);

      await loadContracts();
      await loadStats();

      toast.success("Contract has been created successfully.");
    } catch (error: any) {
      console.error('Error creating contract:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;

    const selectedClient = clients.find(c => (c.id === editContractClientId) || (c._id === editContractClientId));
    const clientName = selectedClient ? selectedClient.name : '';

    if (!editContract.title || !editContractClientId || !editContract.startDate || !editContract.endDate) {
      return;
    }

    try {
      setSubmitting(true);
      const contractData = {
        ...editContract,
        client: clientName,
      };
      await contractService.updateContract(selected.id, contractData);

      setShowEdit(false);

      await loadContracts();
      await loadStats();

      toast.success("Contract has been updated successfully.");
    } catch (error: any) {
      console.error('Error updating contract:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;

    try {
      setSubmitting(true);
      await contractService.deleteContract(selected.id);

      setShowDelete(false);
      setSelected(null);

      await loadContracts();
      await loadStats();

      toast.success(`${selected.title} has been deleted successfully.`);
    } catch (error: any) {
      console.error('Error deleting contract:', error);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contracts</h1>
          <p className="text-muted-foreground">
            Manage client contracts and agreements
          </p>
        </div>
        {hasSubModulePermission('sales_clients', 'contract', 'create') && (
          <Button onClick={() => setShowAdd(true)} className="rounded-xl">
            <Plus className="h-4 w-4 mr-2" />
            Create Contract
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeContracts}</div>
            <p className="text-xs text-muted-foreground">
              Currently active
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.totalValue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Active contracts
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expiringSoon}</div>
            <p className="text-xs text-muted-foreground">
              Next 30 days
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Renewals</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingRenewals}</div>
            <p className="text-xs text-muted-foreground">
              Require attention
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Contract List</CardTitle>
              <CardDescription>View and manage your contracts</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search contracts..."
                  className="pl-8"
                  onChange={handleSearchChange}
                />
              </div>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[120px] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 rows</SelectItem>
                  <SelectItem value="10">10 rows</SelectItem>
                  <SelectItem value="25">25 rows</SelectItem>
                  <SelectItem value="50">50 rows</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={handleRefresh} className="rounded-xl">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Renewal Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading contracts...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : contracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No contracts found
                    </TableCell>
                  </TableRow>
                ) : (
                  contracts.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell className="font-medium">{contract.title}</TableCell>
                      <TableCell>{contract.client}</TableCell>
                      <TableCell>${contract.value.toLocaleString()}</TableCell>
                      <TableCell>{contract.type}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(contract.status)}>
                          {contract.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{contract.startDate}</TableCell>
                      <TableCell>{contract.endDate}</TableCell>
                      <TableCell>{contract.renewalDate}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openView(contract)}>
                              <FileText className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {hasSubModulePermission('sales_clients', 'contract', 'edit') && (
                              <DropdownMenuItem onClick={() => openEdit(contract)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Contract
                              </DropdownMenuItem>
                            )}
                            {hasSubModulePermission('sales_clients', 'contract', 'delete') && (
                              <DropdownMenuItem onClick={() => openDelete(contract)} className="text-red-600 focus:text-red-600">
                                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                                Delete Contract
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <div className="flex items-center justify-between px-6 pb-4">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, totalContracts)} of {totalContracts} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded-xl"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {(() => {
                const pageButtons: JSX.Element[] = [];

                if (totalPages > 3) {
                  let startPage = Math.max(1, currentPage - 1);
                  let endPage = Math.min(totalPages, currentPage + 1);

                  if (startPage === 1) {
                    endPage = Math.min(totalPages, 3);
                  }
                  if (endPage === totalPages) {
                    startPage = Math.max(1, totalPages - 2);
                  }

                  if (startPage > 1) {
                    pageButtons.push(
                      <Button
                        key={1}
                        variant={1 === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        1
                      </Button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis-start" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis-end" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                    pageButtons.push(
                      <Button
                        key={totalPages}
                        variant={totalPages === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    );
                  }
                } else {
                  for (let i = 1; i <= totalPages; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }
                }

                return pageButtons;
              })()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded-xl"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </Card>

      {/* View Contract Dialog */}
      <ViewContractDialog contract={selected} open={showView} onOpenChange={setShowView} />

      {/* Add Contract Modal */}
      <Dialog open={showAdd} onOpenChange={(open) => {
        setShowAdd(open);
        if (!open) {
          setNewContractErrors({ title: '', client: '', startDate: '', endDate: '' });
          setNewContractClientId('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Create Contract</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="c-title">Title *</Label>
                  <Input id="c-title" placeholder="Enter contract title" value={newContract.title} onChange={(e) => { setNewContract({ ...newContract, title: e.target.value }); setNewContractErrors(prev => ({ ...prev, title: '' })); }} className="rounded-xl" />
                  {newContractErrors.title && <div className="text-red-600 text-xs mt-1">{newContractErrors.title}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-client">Client *</Label>
                  <Select
                    value={newContractClientId}
                    onValueChange={(value) => {
                      setNewContractClientId(value);
                      setNewContractErrors(prev => ({ ...prev, client: '' }));
                    }}
                  >
                    <SelectTrigger className="w-full h-10 border-gray-300 rounded-xl">
                      <SelectValue placeholder="Select and begin typing" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingClients ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading clients from API...
                        </div>
                      ) : clients.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No clients available from API
                        </div>
                      ) : (
                        clients.map((client) => {
                          const clientId = client.id || client._id || '';
                          return (
                            <SelectItem key={clientId} value={clientId}>
                              <div className="flex items-center justify-between w-full">
                                <span className="font-medium">{client.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {newContractErrors.client && <div className="text-red-600 text-xs mt-1">{newContractErrors.client}</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Value ($)</Label>
                  <Input type="number" min="0" step="0.01" value={newContract.value} onChange={(e) => setNewContract({ ...newContract, value: parseFloat(e.target.value) || 0 })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newContract.status} onValueChange={(v: 'Active' | 'Pending' | 'Expired') => setNewContract({ ...newContract, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Input value={newContract.type} onChange={(e) => setNewContract({ ...newContract, type: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input type="date" value={newContract.startDate} onChange={(e) => { setNewContract({ ...newContract, startDate: e.target.value }); setNewContractErrors(prev => ({ ...prev, startDate: '' })); }} className="rounded-xl" />
                  {newContractErrors.startDate && <div className="text-red-600 text-xs mt-1">{newContractErrors.startDate}</div>}
                </div>
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input type="date" value={newContract.endDate} onChange={(e) => { setNewContract({ ...newContract, endDate: e.target.value }); setNewContractErrors(prev => ({ ...prev, endDate: '' })); }} className="rounded-xl" />
                  {newContractErrors.endDate && <div className="text-red-600 text-xs mt-1">{newContractErrors.endDate}</div>}
                </div>
                <div className="space-y-2">
                  <Label>Renewal Date</Label>
                  <Input type="date" value={newContract.renewalDate} onChange={(e) => setNewContract({ ...newContract, renewalDate: e.target.value })} className="rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={showEdit} onOpenChange={(open) => {
        setShowEdit(open);
        if (!open) {
          setEditContractErrors({ title: '', client: '', startDate: '', endDate: '' });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Edit Contract</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="e-title">Title *</Label>
                  <Input id="e-title" placeholder="Enter contract title" value={editContract.title} onChange={(e) => setEditContract({ ...editContract, title: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e-client">Client *</Label>
                  <Select
                    value={editContractClientId}
                    onValueChange={(value) => {
                      setEditContractClientId(value);
                      setEditContractErrors(prev => ({ ...prev, client: '' }));
                    }}
                  >
                    <SelectTrigger className="w-full h-10 border-gray-300 rounded-xl">
                      <SelectValue placeholder="Select and begin typing" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingClients ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading clients from API...
                        </div>
                      ) : clients.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No clients available from API
                        </div>
                      ) : (
                        clients.map((client) => {
                          const clientId = client.id || client._id || '';
                          return (
                            <SelectItem key={clientId} value={clientId}>
                              <div className="flex items-center justify-between w-full">
                                <span className="font-medium">{client.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {editContractErrors.client && <div className="text-red-600 text-xs mt-1">{editContractErrors.client}</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Value ($)</Label>
                  <Input type="number" min="0" step="0.01" value={editContract.value} onChange={(e) => setEditContract({ ...editContract, value: parseFloat(e.target.value) || 0 })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editContract.status} onValueChange={(v: 'Active' | 'Pending' | 'Expired') => setEditContract({ ...editContract, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Input value={editContract.type} onChange={(e) => setEditContract({ ...editContract, type: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input type="date" value={editContract.startDate} onChange={(e) => setEditContract({ ...editContract, startDate: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input type="date" value={editContract.endDate} onChange={(e) => setEditContract({ ...editContract, endDate: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Renewal Date</Label>
                  <Input type="date" value={editContract.renewalDate} onChange={(e) => setEditContract({ ...editContract, renewalDate: e.target.value })} className="rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contract?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              {selected ? ` ${selected.title}` : ' this contract'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 